/**
 * The guide answers in Markdown, and rendering it is an OBLIGATION, not a
 * nicety: `docs/UI_CONTRACT.md` §0 says so, and raw `**звёздочки**` on screen is
 * a frontend bug. The route block in particular depends on it — a break rides
 * as a nested list item, and a renderer that ignores markdown runs «🕐
 * 10:00-19:45» into «перекус 30 мин», which reads as a snack at a quarter to
 * eight.
 *
 * `marked` parses, `DOMPurify` decides what survives. The CSP forbids inline
 * script anyway, so the sanitiser is the second wall rather than the only one.
 */

import { useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { stripEmoji } from '../lib/emoji';

marked.setOptions({ gfm: true, breaks: true });

// GFM lets a SINGLE tilde strike text out, and the guide writes «~40 минут»
// for an estimate — so «Большой каскад (~40 минут) → Монплезир (~30 минут)»
// rendered with the whole span between the tildes crossed out. Strikethrough
// is kept, but only behind the double tilde nobody writes by accident.
marked.use({
  tokenizer: {
    del(src) {
      const match = /^~~(?=\S)([\s\S]*?\S)~~/.exec(src);
      const text = match?.[1];
      if (!match || text === undefined) return undefined;
      return { type: 'del', raw: match[0], text, tokens: this.lexer.inlineTokens(text) };
    },
  },
});

// Links leave in a new tab, and `noopener` is added on the sanitiser's way out
// so a hook cannot be forgotten at a call site.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noreferrer noopener');
  }
});

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'em', 'del', 'code', 'pre', 'blockquote',
  'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'h4', 'hr',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
];

export default function Markdown({ text }: { text: string }) {
  const html = useMemo(() => {
    // The visitor sees no emoji: taken out of what is DRAWN (`lib/emoji.ts`).
    const shown = stripEmoji(text);
    try {
      const parsed = marked.parse(shown, { async: false }) as string;
      return DOMPurify.sanitize(parsed, {
        ALLOWED_TAGS,
        ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
        // No `data:` or `javascript:` hrefs; the guide only ever links out.
        ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:)/i,
      });
    } catch {
      // A parser failure must not cost the answer: plain text still reads.
      return DOMPurify.sanitize(shown.replace(/[<>&]/g, (c) =>
        c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&amp;',
      ));
    }
  }, [text]);

  return <div className="md" dangerouslySetInnerHTML={{ __html: html }} />;
}
